import { fmtDateTime } from "./visitorUtils";

// Carnet de visita dibujado en canvas: se usa tanto para la vista previa como para la descarga
const W = 640;
const H = 1000;
const NAVY = "#0F172A";
const ORANGE = "#F97316";

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Escribe texto centrado reduciendo la fuente hasta que quepa en el ancho dado
const fitText = (ctx, text, y, maxWidth, size, weight = "700", color = NAVY, minSize = 18) => {
  if (!text) return;
  let s = size;
  ctx.font = `${weight} ${s}px "IBM Plex Sans", Arial, sans-serif`;
  while (ctx.measureText(text).width > maxWidth && s > minSize) {
    s -= 2;
    ctx.font = `${weight} ${s}px "IBM Plex Sans", Arial, sans-serif`;
  }
  let out = text;
  while (ctx.measureText(out).width > maxWidth && out.length > 3) out = out.slice(0, -2);
  if (out !== text) out = `${out.trim()}…`;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.fillText(out, W / 2, y);
};

/**
 * Genera el carnet del visitante como PNG (data URL).
 * labels: textos traducidos { title, host, validFrom, validUntil, uses, footer }
 */
export const buildPassImage = async ({ preauth, qrCanvas, labels }) => {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Tarjeta y perforación del lanyard
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, 0, 0, W, H, 36);
  ctx.fill();
  ctx.save();
  roundRect(ctx, 0, 0, W, H, 36);
  ctx.clip();

  // Cabecera con franjas diagonales
  const headerH = 300;
  ctx.fillStyle = NAVY;
  ctx.fillRect(0, 0, W, headerH);
  ctx.fillStyle = "rgba(249,115,22,0.85)";
  ctx.beginPath();
  ctx.moveTo(W * 0.55, 0);
  ctx.lineTo(W, 0);
  ctx.lineTo(W, headerH * 0.75);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.06)";
  ctx.beginPath();
  ctx.moveTo(0, headerH * 0.55);
  ctx.lineTo(W * 0.45, headerH);
  ctx.lineTo(0, headerH);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = "#E2E8F0";
  roundRect(ctx, W / 2 - 50, 26, 100, 18, 9);
  ctx.fill();

  ctx.textAlign = "center";
  ctx.fillStyle = ORANGE;
  ctx.font = `700 22px "IBM Plex Sans", Arial, sans-serif`;
  ctx.fillText(labels.title.toUpperCase(), W / 2, 92);
  fitText(ctx, preauth.company_name || "Sentinel Pro", 134, W - 80, 34, "700", "#FFFFFF");
  fitText(ctx, preauth.facility_name, 172, W - 80, 24, "500", "#CBD5E1");

  // Foto circular que sobresale de la cabecera
  const photoR = 110;
  const photoY = headerH;
  ctx.beginPath();
  ctx.arc(W / 2, photoY, photoR + 10, 0, Math.PI * 2);
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  const photo = await loadImage(preauth.visitor_photo);
  ctx.save();
  ctx.beginPath();
  ctx.arc(W / 2, photoY, photoR, 0, Math.PI * 2);
  ctx.clip();
  if (photo) {
    const scale = Math.max((photoR * 2) / photo.width, (photoR * 2) / photo.height);
    const pw = photo.width * scale;
    const ph = photo.height * scale;
    ctx.drawImage(photo, W / 2 - pw / 2, photoY - ph / 2, pw, ph);
  } else {
    ctx.fillStyle = "#E2E8F0";
    ctx.fillRect(W / 2 - photoR, photoY - photoR, photoR * 2, photoR * 2);
    ctx.fillStyle = "#64748B";
    ctx.font = `700 90px "IBM Plex Sans", Arial, sans-serif`;
    ctx.textBaseline = "middle";
    const initials = (preauth.visitor_name || "?")
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();
    ctx.fillText(initials, W / 2, photoY + 4);
    ctx.textBaseline = "alphabetic";
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(W / 2, photoY, photoR + 5, 0, Math.PI * 2);
  ctx.strokeStyle = ORANGE;
  ctx.lineWidth = 6;
  ctx.stroke();

  // Datos del visitante
  fitText(ctx, (preauth.visitor_name || "").toUpperCase(), 480, W - 70, 44, "800");
  fitText(ctx, preauth.visitor_company, 520, W - 80, 24, "500", ORANGE);
  const hostLine = [preauth.host_name, preauth.host_unit].filter(Boolean).join(" · ");
  if (hostLine) fitText(ctx, `${labels.host}: ${hostLine}`, 556, W - 80, 22, "500", "#475569");

  // QR
  const qrSize = 250;
  const qrY = 585;
  ctx.fillStyle = "#F8FAFC";
  roundRect(ctx, W / 2 - qrSize / 2 - 16, qrY - 16, qrSize + 32, qrSize + 32, 18);
  ctx.fill();
  if (qrCanvas) ctx.drawImage(qrCanvas, W / 2 - qrSize / 2, qrY, qrSize, qrSize);

  // Vigencia
  const validY = qrY + qrSize + 52;
  ctx.font = `600 19px "IBM Plex Sans", Arial, sans-serif`;
  ctx.fillStyle = "#475569";
  ctx.textAlign = "center";
  ctx.fillText(`${labels.validFrom}: ${fmtDateTime(preauth.valid_from)}`, W / 2, validY);
  ctx.fillText(`${labels.validUntil}: ${fmtDateTime(preauth.valid_until)}`, W / 2, validY + 26);

  // Pie con identificador
  ctx.fillStyle = ORANGE;
  ctx.fillRect(0, H - 56, W, 56);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `700 22px "IBM Plex Sans", Arial, sans-serif`;
  ctx.fillText(`ID: ${preauth.id.slice(0, 8).toUpperCase()}  ·  ${labels.footer}`, W / 2, H - 20);

  ctx.restore();
  return canvas.toDataURL("image/png");
};
